/**
 * AttachmentTray — the staged-files strip shared by every Merlin composer.
 *
 * Renders the files staged for the next message (image thumbnails, doc chips)
 * with per-file remove, a paperclip picker (multiple, backend-whitelist
 * `accept`), any screening error, and — while a file drag hovers the composer —
 * a "Drop files to attach" veil. Presentational + controlled: the page owns the
 * state via `useComposerAttachments` and does the uploading on send.
 */
import { useEffect, useMemo, useRef } from 'react';
import {
  ACCEPT_ATTR,
  effectiveMimeType,
  formatBytes,
  isImageMime,
} from './composerAttachments';
import styles from './AttachmentTray.module.css';

function Thumb({ file }: { file: File }) {
  // Object URLs leak without an explicit revoke — tie one to the chip's life.
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return <img className={styles.thumb} src={url} alt="" aria-hidden="true" />;
}

export function AttachmentTray({
  files,
  attachError,
  onAddFiles,
  onRemoveFile,
  disabled = false,
}: {
  files: File[];
  attachError: string | null;
  onAddFiles: (files: FileList | null) => void;
  onRemoveFile: (index: number) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className={styles.tray}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        className={styles.hiddenInput}
        onChange={(e) => {
          onAddFiles(e.target.files);
          e.target.value = ''; // re-picking the same file must fire again
        }}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        className={styles.attachBtn}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title="Attach images or files (or drag & drop / paste)"
        aria-label="Attach images or files"
      >
        📎
      </button>
      {files.length > 0 && (
        <ul className={styles.list}>
          {files.map((f, i) => (
            <li className={styles.chip} key={`${f.name}-${f.size}-${i}`}>
              {isImageMime(effectiveMimeType(f)) ? (
                <Thumb file={f} />
              ) : (
                <span className={styles.docIcon} aria-hidden="true">
                  📄
                </span>
              )}
              <span className={styles.name} title={f.name}>
                {f.name}
              </span>
              <span className={styles.size}>{formatBytes(f.size)}</span>
              <button
                type="button"
                className={styles.remove}
                onClick={() => onRemoveFile(i)}
                disabled={disabled}
                aria-label={`Remove ${f.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      {attachError && (
        <span className={styles.error} role="alert">
          {attachError}
        </span>
      )}
    </div>
  );
}

/** Full-composer veil shown while a file drag hovers the drop zone. */
export function DropVeil({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <div className={styles.veil} aria-hidden="true">
      <span className={styles.veilText}>Drop files to attach</span>
    </div>
  );
}
